import { Box, Stack, Typography } from "@mui/material";
import { CaseFile } from "../../../pages/cases/types";
import { FileUpload } from "../types";
import { Circle, TextSnippetOutlined } from "@mui/icons-material";
import { cutOffText, formatSize } from "../../../utils";
import { openFileInNewTab } from "../../../pages/cases/utils";
import DotsMenu from "../../buttons/DotsMenu";
import Button from "../../buttons/Button";
import { ReactElement } from "react";
import Translatetions from "../../../lang/en/pages.json";

type SupportedFileTypes = "pdf" | "docx" | "doc" | "txt" | "rtf";

const iconByFileType = (file: string | CaseFile | FileUpload): ReactElement => {
  let fileName: string;

  if (typeof file === "string") {
    fileName = file;
  } else if ("filename" in file) {
    fileName = file.filename;
  } else if ("file" in file && file.file instanceof File) {
    fileName = file.file.name;
  } else {
    fileName = "";
  }

  const extension = fileName.split(".").pop()?.toLowerCase();

  return (
    {
      pdf: <TextSnippetOutlined />,
      docx: <TextSnippetOutlined />,
      doc: <TextSnippetOutlined />,
      txt: <TextSnippetOutlined />,
      rtf: <TextSnippetOutlined />,
      default: <TextSnippetOutlined />,
    }[extension as SupportedFileTypes] || <TextSnippetOutlined />
  );
};

interface UploadedFileListProps {
  files: (CaseFile | FileUpload)[];
  onDelete: (file: CaseFile | FileUpload) => void;
  disabled?: boolean;
  getPreviewUrl?: (file: CaseFile) => Promise<string | null | undefined>;
}

const UploadedFileList: React.FC<UploadedFileListProps> = ({
  files,
  onDelete,
  disabled = false,
  getPreviewUrl,
}) => {
  const filesMaxHeight = window.outerHeight * 0.25;

  return (
    <Stack maxHeight={filesMaxHeight} overflow="auto" spacing={1}>
      {files.map((file, index) => {
        const fileName =
          "filename" in file
            ? file.filename
            : file.file?.name || "Unknown File";

        // Server files (CaseFile) carry a raw storage path — presign on demand.
        const isServerFile = "id" in file;
        const fileUrl = isServerFile
          ? ""
          : "url" in file
          ? file.url
          : (file as FileUpload).file
          ? URL.createObjectURL((file as FileUpload).file)
          : "";

        const handlePreview = () => {
          if (isServerFile) {
            if (getPreviewUrl) {
              void openFileInNewTab(() => getPreviewUrl(file as CaseFile));
            }
            return;
          }
          if (fileUrl) window.open(fileUrl, "_blank");
        };

        return (
          <Stack
            key={`${fileName}-${index}`}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ backgroundColor: "#F6FBF4" }}
            p={0}
          >
            <Stack direction="row" spacing={4} alignItems="center">
              <Box height={25} color="#00796B">
                {iconByFileType(file)}
              </Box>
              <Stack alignItems="center" direction="row" spacing={1}>
                <Box maxWidth="100%">
                  <Typography fontWeight="light" variant="body2">
                    {cutOffText(fileName, 50, "middle")}
                  </Typography>
                </Box>
                <Circle sx={{ fontSize: 5 }} fontSize="small" />
                <Button
                  sx={{ ml: "0!important" }}
                  width="auto"
                  variant="text"
                  disabled={isServerFile && !getPreviewUrl}
                  onClick={handlePreview}
                >
                  <Typography variant="body2" fontWeight="light">
                    {Translatetions.pages.new_case_files_page.buttons.preview}
                  </Typography>
                </Button>
              </Stack>
            </Stack>

            <Stack direction="row" spacing={0} alignItems="center">
              <Typography variant="body2" fontWeight="light">
                {"file" in file
                  ? formatSize((file as FileUpload)?.file.size || 0)
                  : null}
              </Typography>
              <DotsMenu
                onChange={(option) => {
                  if (option.toString() === "delete") {
                    onDelete(file);
                  }
                }}
                options={[
                  {
                    label:
                      Translatetions.pages.new_case_files_page.buttons.preview,
                    value: "rename",
                  },
                  {
                    label:
                      Translatetions.pages.new_case_files_page.buttons.delete,
                    value: "delete",
                  },
                ]}
              />
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  );
};

export default UploadedFileList;
